import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { LanguageService } from '../services/language.service';

/**
 * Attaches the bearer token and the tenant context headers to every API call,
 * and retries once through /auth/refresh when the access token has expired.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  // Only after the early return: LanguageService's own constructor loads the
  // stored language's translation file, and that request comes through here.
  // Asking for the service before letting it pass would ask for a service that
  // is still being built — a circular dependency that cost a Kannada device
  // its first load of Kannada.
  const language = inject(LanguageService);

  // Before everything else, including the early return for sign-in: a wrong
  // password has to be refused in the language on screen, and at that moment
  // there is no account to read a preference from.
  req = req.clone({ headers: req.headers.set('Accept-Language', language.current()) });

  const isAuthCall = req.url.includes('/auth/login') || req.url.includes('/auth/refresh');

  const withContext = (token: string | null) => {
    let headers = req.headers;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const user = auth.user();
    if (user) {
      if (user.school_id !== null) headers = headers.set('X-School-Id', String(user.school_id));
      // A school-wide admin's branch is the one they have selected; everyone
      // else's is fixed on their account. The server resolves that branch's
      // current year, so no year header is needed alongside it.
      const branchId = auth.effectiveBranchId();
      if (branchId !== null) {
        headers = headers.set('X-Branch-Id', String(branchId));
      }
      // A school-wide admin's stored academic_year_id belongs to whichever
      // branch they last touched, so sending it while switching branches would
      // pin the wrong year — the server resolves the branch's current year
      // instead. Everyone else's year matches their fixed branch.
      if (
        user.role !== 'SCHOOL_ADMIN' &&
        user.branch_id !== null &&
        user.academic_year_id !== null
      ) {
        headers = headers.set('X-Academic-Year-Id', String(user.academic_year_id));
      }
    }
    return req.clone({ headers });
  };

  if (isAuthCall) {
    return next(req);
  }

  return next(withContext(auth.accessToken)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || !auth.refreshToken) {
        return throwError(() => error);
      }
      return auth.refresh().pipe(
        switchMap(() => next(withContext(auth.accessToken))),
        catchError(refreshError => {
          auth.logout();
          return throwError(() => refreshError);
        })
      );
    })
  );
};
